import { useState, useEffect, useRef, useCallback } from 'react';
import { tasksAPI } from '../api/tasks.api';
import { useAppStore } from '@/app/store';
import { useSocket } from '@/contexts/SocketContext';
import { useUnreadContext } from '../contexts/UnreadContext';
import { playNotificationSound } from '@/utils/notificationSound';
import { useSettings } from '@/features/shared/settings/hooks/useSettings';
import type { Task } from '../types/task.types';

interface UseTaskNotificationsOptions {
  enabled?: boolean;
  pollingInterval?: number;
}

export const useTaskNotifications = ({
  enabled = true,
  pollingInterval = 30000 // 30 seconds as fallback (increased from 5s since we have WebSocket)
}: UseTaskNotificationsOptions = {}) => {
  const [lastCheckTime, setLastCheckTime] = useState(() => Date.now());
  const [isCheckingForNew, setIsCheckingForNew] = useState(false);
  const addNotification = useAppStore(state => state.addNotification);
  const { refetchUnreadCount } = useUnreadContext();
  const notifiedEvents = useRef<Set<string>>(new Set());
  const MAX_NOTIFIED_EVENTS = 1000; // Prevent unlimited growth
  const { socket, isConnected } = useSocket();
  const { settings } = useSettings();

  // Request browser notification permission
  useEffect(() => {
    if (enabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [enabled]);

  // Check for new tasks
  const checkForNewTasks = async () => {
    if (!enabled || isCheckingForNew) return;

    try {
      setIsCheckingForNew(true);
      const response = await tasksAPI.getNewTasksSince(lastCheckTime);
      
      if (response.success && response.data.new_tasks.length > 0) {
        const newTasks = response.data.new_tasks.filter(task => {
          // Skip tasks linked to affaires - handled by tracker notifications
          if (task.k_id_affaire) {
            return false;
          }
          const eventKey = `poll-task-${task.id}-${task.created_at}`;
          return !notifiedEvents.current.has(eventKey);
        });

        if (newTasks.length > 0) {
          // Show notifications for each new task
          newTasks.forEach(task => {
            const eventKey = `poll-task-${task.id}-${task.created_at}`;
            showTaskNotification(task);
            notifiedEvents.current.add(eventKey);
            
            // Prevent unlimited growth - remove oldest entries
            if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
              const firstKey = notifiedEvents.current.values().next().value;
              if (firstKey) {
                notifiedEvents.current.delete(firstKey);
              }
            }
          });

          // Update last check time to most recent task
          const mostRecentTask = newTasks[0];
          const taskTime = new Date(mostRecentTask.created_at).getTime();
          setLastCheckTime(Math.max(taskTime, Date.now()));
        }
      }
    } catch (error) {
      console.error('Error checking for new tasks:', error);
    } finally {
      setIsCheckingForNew(false);
    }
  };

  // Show notification for a task
  const showTaskNotification = useCallback((task: Task, createdByName?: string) => {
    const title = 'Nouvelle tâche assignée';
    const message = task.message.length > 100 
      ? task.message.substring(0, 100) + '...' 
      : task.message;
    const createdBy = createdByName || task.created_by_name || 'Inconnu';

    // In-app notification
    addNotification({
      type: 'info',
      title,
      message: `${createdBy}: ${message}`
    });

    // Play notification sound if enabled (default to true if settings not loaded)
    console.log('[TaskNotifications] Settings:', settings, 'soundNotificationsEnabled:', settings?.soundNotificationsEnabled);
    if (settings?.soundNotificationsEnabled !== false) {
      console.log('[TaskNotifications] Playing notification sound');
      playNotificationSound();
    } else {
      console.log('[TaskNotifications] Sound disabled by user settings');
    }

    // Browser notification if permission granted
    if ('Notification' in window && Notification.permission === 'granted') {
      try {
        const notification = new Notification(title, {
          body: `${createdBy}: ${message}`,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
          tag: `task-${task.id}`,
          requireInteraction: false,
          silent: false
        });

        // Auto-close after 5 seconds
        setTimeout(() => {
          notification.close();
        }, 5000);

        // Handle click - focus window and navigate to tasks
        notification.onclick = () => {
          window.focus();
          // Navigate to tasks tab if possible
          const taskUrl = `/admin/mes-taches`;
          if (window.location.pathname !== taskUrl) {
            window.location.href = taskUrl;
          }
          notification.close();
        };
      } catch (error) {
        console.error('Error showing browser notification:', error);
      }
    }
  }, [addNotification]);

  // Set up WebSocket listeners
  useEffect(() => {
    if (!enabled || !socket) {
      return;
    }

    const handleTaskCreated = (data: {
      task: Task;
      createdBy: { id: string; name: string };
      timestamp: string;
    }) => {
      console.log('[TaskNotifications] Received task:created event:', data);

      // Skip tasks linked to affaires - handled by tracker notifications
      if (data.task.k_id_affaire) {
        console.log('[TaskNotifications] Skipping affaire-linked task:', data.task.id);
        return;
      }

      // Use event-specific key to allow multiple notifications per task
      const eventKey = `task:created-${data.task.id}-${data.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        console.log('[TaskNotifications] Already notified for event:', eventKey);
        return;
      }
      
      showTaskNotification(data.task, data.createdBy.name);
      notifiedEvents.current.add(eventKey);
      
      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }

      // Update last check time to prevent duplicate notifications from polling
      setLastCheckTime(new Date(data.timestamp).getTime());
    };

    const handleTaskCommentNotification = (data: {
      taskId: string;
      comment: any;
      taskTitle: string;
    }) => {
      
      // Use comment-specific key to allow multiple comment notifications
      const eventKey = `comment-${data.taskId}-${data.comment.id}-${Date.now()}`;
      if (notifiedEvents.current.has(eventKey)) {
        return;
      }
      
      const title = 'Nouveau commentaire';
      const message = `${data.comment.user_name}: ${data.comment.message.substring(0, 100)}${data.comment.message.length > 100 ? '...' : ''}`;
      
      // Show in-app notification
      addNotification({
        type: 'info',
        title,
        message
      });

      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }

      // Play notification sound if enabled
      if (settings?.soundNotificationsEnabled) {
        playNotificationSound();
      }

      // Browser notification if permission granted
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const notification = new Notification(title, {
            body: message,
            icon: '/favicon.ico',
            badge: '/favicon.ico',
            tag: `comment-${data.taskId}-${data.comment.id}`,
            requireInteraction: false,
            silent: false
          });

          // Auto-close after 5 seconds
          setTimeout(() => {
            notification.close();
          }, 5000);

          // Handle click - focus window and navigate to task
          notification.onclick = () => {
            window.focus();
            // Navigate to tasks tab with the specific task open
            const taskUrl = `/admin/mes-taches?taskId=${data.taskId}`;
            if (window.location.pathname !== '/admin/mes-taches') {
              window.location.href = taskUrl;
            }
            notification.close();
          };
        } catch (error) {
          console.error('Error showing browser notification:', error);
        }
      }
      
      // Refetch unread count to update navigation badge
      refetchUnreadCount();
    };
    
    // Also listen for comment:added event (might be used in some cases)
    const handleCommentAdded = (data: {
      taskId: string;
      comment: any;
      timestamp: string;
    }) => {
      console.log('[TaskNotifications] Received comment:added event:', data);
      // This might be a broadcast to all users in the task room
      // We might want to show a notification here too if needed
    };

    // Handle chat messages
    const handleChatMessage = (data: {
      threadId: string;
      message: any;
      affaireId: string;
      timestamp: string;
    }) => {
      console.log('[TaskNotifications] Received chat:message event:', data);

      // Skip chat messages related to affaires - handled by tracker notifications
      if (data.affaireId) {
        console.log('[TaskNotifications] Skipping affaire-linked chat message');
        return;
      }

      // Use unique key for each chat message
      const eventKey = `chat-${data.message.id}-${data.timestamp}`;
      if (notifiedEvents.current.has(eventKey)) {
        return;
      }
      
      // Show notification for chat message
      addNotification({
        type: 'info',
        title: 'Nouveau message chat',
        message: `${data.message.first_name} ${data.message.last_name}: ${data.message.message.substring(0, 100)}${data.message.message.length > 100 ? '...' : ''}`
      });

      notifiedEvents.current.add(eventKey);

      // Prevent unlimited growth
      if (notifiedEvents.current.size > MAX_NOTIFIED_EVENTS) {
        const firstKey = notifiedEvents.current.values().next().value;
        if (firstKey) {
          notifiedEvents.current.delete(firstKey);
        }
      }

      // Play notification sound if enabled
      if (settings?.soundNotificationsEnabled) {
        playNotificationSound();
      }
    };

    // Listen to WebSocket events
    socket.on('task:created', handleTaskCreated);
    socket.on('task:comment:notification', handleTaskCommentNotification);
    socket.on('comment:added', handleCommentAdded);
    socket.on('chat:message', handleChatMessage);
    
    console.log('[TaskNotifications] WebSocket listeners attached');

    return () => {
      socket.off('task:created', handleTaskCreated);
      socket.off('task:comment:notification', handleTaskCommentNotification);
      socket.off('comment:added', handleCommentAdded);
      socket.off('chat:message', handleChatMessage);
      console.log('[TaskNotifications] WebSocket listeners removed');
    };
  }, [enabled, socket, showTaskNotification, addNotification, refetchUnreadCount]);

  // Set up polling as fallback (with longer interval since we have WebSocket)
  useEffect(() => {
    if (!enabled) return;
    
    // Only use polling if WebSocket is not connected
    if (isConnected) return;

    // Initial check
    checkForNewTasks();

    // Set up interval for fallback polling
    const interval = setInterval(checkForNewTasks, pollingInterval);

    return () => clearInterval(interval);
  }, [enabled, pollingInterval, lastCheckTime, isConnected]);

  // Handle visibility change - check when tab becomes visible
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkForNewTasks();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [enabled]);

  return {
    checkForNewTasks,
    isCheckingForNew,
    lastCheckTime
  };
};